import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import AsyncSelect from 'react-select/async'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { Button, InputGroup, Select } from 'components/ui'
import useThemePalette from 'utils/hooks/useThemePalette'
import { apiSearchOpenTickets } from 'services/TicketService'

const OPEN_TICKET_SEARCH_LIMIT = 12

const getReferenceName = (reference) => {
    return reference?.name || reference?.identifier || ''
}

const formatTicketOption = (ticket) => {
    const summary = ticket?.summary || 'Untitled ticket'

    return {
        value: ticket?.id,
        label: `#${ticket?.id} — ${summary}`,
        ticket,
    }
}

const renderTicketOption = (option, { context }) => {
    const ticket = option?.ticket || {}
    const companyName = getReferenceName(ticket.company)
    const statusName = getReferenceName(ticket.status)
    const contactName = getReferenceName(ticket.contact)
    const summary = ticket.summary || 'Untitled ticket'

    if (context === 'value') {
        return <span className="truncate">#{ticket.id} — {summary}</span>
    }

    return (
        <div className="py-0.5 min-w-0">
            <div className="font-medium text-gray-900 dark:text-gray-100 truncate">
                #{ticket.id} {summary}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                {[companyName, statusName, contactName].filter(Boolean).join(' · ') || 'Open ticket'}
            </div>
        </div>
    )
}

const TicketSearchControl = ({
    className,
    placeholder,
    variant,
}) => {
    const navigate = useNavigate()
    const palette = useThemePalette()
    const signedIn = useSelector((state) => Boolean(state.auth.session?.signedIn))
    const [searchText, setSearchText] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)

    const loadOptions = useCallback(
        async (inputValue) => {
            if (!signedIn) {
                return []
            }

            const query = String(inputValue || '').trim()
            if (!query) {
                return []
            }

            try {
                const response = await apiSearchOpenTickets({
                    query,
                    limit: OPEN_TICKET_SEARCH_LIMIT,
                })
                const tickets = response?.data?.data?.tickets

                return Array.isArray(tickets)
                    ? tickets.map(formatTicketOption).filter((option) => option.value)
                    : []
            } catch (error) {
                return []
            }
        },
        [signedIn]
    )

    const handleSelectTicket = useCallback(
        (option) => {
            if (!option?.value) {
                return
            }

            navigate(`/tickets/${option.value}`)
        },
        [navigate]
    )

    const handleSubmitSearch = useCallback(async () => {
        const query = searchText.trim()
        if (!query) {
            return
        }

        setIsSubmitting(true)
        try {
            const options = await loadOptions(query)
            if (options[0]) {
                handleSelectTicket(options[0])
            }
        } finally {
            setIsSubmitting(false)
        }
    }, [handleSelectTicket, loadOptions, searchText])

    const sharedSelectProps = {
        'aria-label': variant === 'hero' ? 'Search for an open ticket from the homepage' : 'Search for an open ticket',
        cacheOptions: true,
        className: classNames('min-w-0 flex-1', className),
        componentAs: AsyncSelect,
        components: {
            DropdownIndicator: () => null,
        },
        defaultOptions: false,
        formatOptionLabel: renderTicketOption,
        isClearable: true,
        loadOptions,
        noOptionsMessage: ({ inputValue }) => {
            if (!inputValue) {
                return variant === 'hero'
                    ? 'Start typing to search open tickets'
                    : 'Type to search open tickets'
            }

            return 'No open tickets found'
        },
        onChange: handleSelectTicket,
        onInputChange: (value, meta) => {
            if (meta.action === 'input-change') {
                setSearchText(value)
            }

            if (meta.action === 'clear' || meta.action === 'set-value') {
                setSearchText('')
            }
        },
        placeholder: placeholder || 'Search open tickets...',
        size: variant === 'hero' ? 'lg' : 'sm',
    }

    if (variant === 'hero') {
        return (
            <div className="w-full">
                <label className="sr-only">Search open tickets</label>
                <InputGroup size="lg" className="w-full">
                    <Select {...sharedSelectProps} />
                    <Button
                        type="button"
                        variant="solid"
                        color={palette.colorToken}
                        loading={isSubmitting}
                        disabled={!searchText.trim()}
                        onClick={handleSubmitSearch}
                    >
                        Go
                    </Button>
                </InputGroup>
            </div>
        )
    }

    return (
        <div className="w-full min-w-0">
            <label className="sr-only">Search open tickets</label>
            <Select {...sharedSelectProps} />
        </div>
    )
}

TicketSearchControl.propTypes = {
    className: PropTypes.string,
    placeholder: PropTypes.string,
    variant: PropTypes.oneOf(['header', 'hero']),
}

TicketSearchControl.defaultProps = {
    className: '',
    placeholder: '',
    variant: 'header',
}

export default TicketSearchControl
